// API utility functions for tenant-aware API calls
// Provides consistent header management across all services

/**
 * Get tenant domain for API calls.
 *
 * Resolution order:
 *   1. If the hostname is `monutrack.dev` / `monutrack.app` OR any
 *      subdomain of them, use the hostname directly — every tenant
 *      lives at `<sub>.monutrack.{dev,app}`, so the subdomain IS the
 *      tenant identity.
 *   2. Otherwise (localhost, Vercel preview URLs, custom hostnames, SSR),
 *      fall back to `VITE_TENANT_DOMAIN`.
 *   3. If no env var is set, default to `monutrack.app`.
 *
 * Only canonical-domain hosts are trusted from the URL; arbitrary
 * hostnames defer to the build-time env var.
 */
export const getTenantDomain = (): string => {
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname
    if (hostname === 'monutrack.dev' || hostname.endsWith('.monutrack.dev')) return hostname
    if (hostname === 'monutrack.app' || hostname.endsWith('.monutrack.app')) return hostname
  }

  // Unknown hostname / localhost / SSR: env var, then default
  const envTenantDomain = import.meta.env.VITE_TENANT_DOMAIN
  if (envTenantDomain) {
    return envTenantDomain
  }

  return 'monutrack.app'
}

/**
 * Get impersonated employee ID from localStorage
 * Used to inject X-Impersonate-Employee header for admin impersonation
 */
const getImpersonatedEmployeeId = (): string | null => {
  if (typeof window === 'undefined') return null
  return localStorage.getItem('impersonation-employee-id')
}

/**
 * Create standard headers for API calls with tenant domain
 * Does NOT include impersonation headers - use createImpersonatedApiHeaders for that
 */
export const createApiHeaders = (additionalHeaders: Record<string, string> = {}): Record<string, string> => {
  const headers: Record<string, string> = {
    Accept: 'application/json',
    'X-Tenant-Domain': getTenantDomain(),
    'Content-Type': 'application/json',
    ...additionalHeaders
  }

  return headers
};

/**
 * Create headers for workforce and monumenta API calls with impersonation support
 * Automatically injects X-Impersonate-Employee header if impersonation is active
 * Use this for /workforce/* and /monumenta/* endpoints only
 */
export const createImpersonatedApiHeaders = (additionalHeaders: Record<string, string> = {}): Record<string, string> => {
  const headers: Record<string, string> = {
    Accept: 'application/json',
    'X-Tenant-Domain': getTenantDomain(),
    'Content-Type': 'application/json',
    ...additionalHeaders
  }

  // Inject impersonation header if active
  const impersonatedEmployeeId = getImpersonatedEmployeeId()
  if (impersonatedEmployeeId) {
    headers['X-Impersonate-Employee'] = impersonatedEmployeeId
  }

  return headers
};

/**
 * Enhanced logging for tenant-aware API calls
 */
export const logApiCall = (path: string, tenantDomain?: string): void => {
  // API call logging available via browser dev tools network tab
};
